import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import ReadOnlyTextField from '@/components/ReadOnlyTextField';
import { resendAccountDetails } from '@/services/UserService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    email: string;
    onClose(): void;
}

export default function ResendAccountDetailsDialog({ email, onClose }: Props) {
    const [isResending, setIsResending] = useState(false);

    const handleResendAccountDetails = async () => {
        setIsResending(true);
        try {
            await resendAccountDetails(email);
            successSnackbar('Account details sent successfully!');
            onClose();
        } catch (error: unknown) {
            console.error('Error resending account details:', error);
            const errorMessage = isAxiosError(error)
                ? (error.response?.data as string)
                : 'Failed to resend account details. Please try again.';
            errorSnackbar(errorMessage);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <Dialog
            open
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && isResending) return;
                onClose();
            }}
        >
            <DialogTitle>Resend Account Details</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ marginBottom: '16px', color: '#7B7D80' }}>
                    Reset password and resend account details for the following user email:
                </Typography>
                <ReadOnlyTextField fullWidth value={email} label="Email" />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isResending}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleResendAccountDetails}
                    loading={isResending}
                    variant="contained"
                >
                    Send
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
